/** Every master screen in the ERP, as declarations.
 *
 * LEGACY_ANALYSIS.md §2 in practice: adding a screen adds an entry here, not
 * a component. GHERP wrote this behaviour out roughly 50 times.
 */

import type { MasterSpec } from '@/types'
import { ITEMS, ITEM_GROUPS, ITEM_SUBGROUPS,
         PRODUCT_FAMILIES, PRODUCT_MODELS, TAX_RATES } from './items'
import { PROCESSES, STAGES } from './routes'
import { SUPPLIERS, CUSTOMERS } from './parties'

export const COMPANIES: MasterSpec = {
  module: 'admin', object: 'company', title: 'Companies',
  singular: 'Company',
  path: 'admin/companies', idKey: 'company_id',
  columns: [
    { key: 'company_id', label: 'Code', width: '120px' },
    { key: 'name',       label: 'Name' },
    { key: 'legal_name', label: 'Legal Name' },
    { key: 'gstin',      label: 'GSTIN', width: '160px' },
  ],
  fields: [
    { key: 'name',       label: 'Name', required: true },
    { key: 'legal_name', label: 'Legal Name' },
    { key: 'gstin',      label: 'GSTIN', help: '15 characters' },
    { key: 'address',    label: 'Address', type: 'textarea' },
  ],
}

export const USERS: MasterSpec = {
  module: 'admin', object: 'user', title: 'Users',
  singular: 'User',
  path: 'admin/users', idKey: 'user_id',
  columns: [
    { key: 'username',    label: 'Username', width: '160px' },
    { key: 'name',        label: 'Name' },
    { key: 'designation', label: 'Designation' },
    { key: 'email',       label: 'Email' },
  ],
  fields: [
    { key: 'username',    label: 'Username', required: true, immutable: true,
      help: 'Cannot be changed once set — it identifies the person in the audit trail' },
    { key: 'name',        label: 'Full Name', required: true },
    { key: 'email',       label: 'Email', type: 'email',
      help: 'Where the invitation and any password reset are sent' },
    { key: 'designation', label: 'Designation' },
  ],
}

export const ROLES: MasterSpec = {
  module: 'admin', object: 'role', title: 'Roles',
  singular: 'Role',
  path: 'admin/roles', idKey: 'role_id',
  columns: [
    { key: 'name',        label: 'Role' },
    { key: 'description', label: 'Description' },
  ],
  fields: [
    { key: 'name',        label: 'Role Name', required: true },
    { key: 'description', label: 'Description', type: 'textarea' },
  ],
}

/** An ERP master, showing what masking looks like in a declaration: the two
 *  commercial columns are marked `value`, so a caller without
 *  `masters.item.view_values` never receives them and sees •••• instead.
 *  LEGACY_ANALYSIS.md §6.1 — Mask is the most-used right in the old system. */

/** The financial years of the current company.
 *
 * `hasStatus: false` on purpose. This table's status is Open/Closed, not
 * Active/Inactive, and closing a year is not an edit — it is a statement that
 * a period is final. It has its own permission and its own action, so it must
 * not appear as an ordinary field somebody with `modify` can change.
 */
export const FINANCIAL_YEARS: MasterSpec = {
  module: 'admin', object: 'financial_year', title: 'Financial Years',
  singular: 'Financial Year',
  path: 'admin/financial-years', idKey: 'financial_year',
  hasStatus: false,
  searchPlaceholder: 'Search years…',
  columns: [
    { key: 'financial_year', label: 'Year', width: '120px' },
    { key: 'starts_on',      label: 'Starts', width: '130px' },
    { key: 'ends_on',        label: 'Ends', width: '130px' },
    { key: 'status',         label: 'Status', width: '110px' },
  ],
  fields: [
    { key: 'financial_year', label: 'Year', required: true, immutable: true,
      help: 'e.g. 2026-27' },
    { key: 'starts_on', label: 'Starts On', type: 'date', required: true },
    { key: 'ends_on',   label: 'Ends On',   type: 'date', required: true },
  ],
}

/** Who may act for the current company.
 *
 * The row IS the access — RIGHTS_MODEL.md §8.1. Creating one here is what
 * lets a person choose this company at sign-in; suspending one takes that
 * away without deleting the history of them having had it.
 *
 * `valid_from` / `valid_to` exist so access can be dated — a contractor for a
 * season, a transfer at month end — rather than remembered and revoked by
 * hand.
 */
export const COMPANY_ACCESS: MasterSpec = {
  module: 'admin', object: 'company_access', title: 'Company Access',
  singular: 'Company Access',
  path: 'admin/company-access', idKey: 'user_id',
  idOptionsFrom: { path: 'admin/users', value: 'user_id', label: 'name' },
  hasStatus: false,
  searchPlaceholder: 'Search by user…',
  columns: [
    { key: 'user_id',    label: 'User', width: '180px' },
    { key: 'status',     label: 'Status', width: '120px' },
    { key: 'valid_from', label: 'Valid From', width: '130px' },
    { key: 'valid_to',   label: 'Valid To', width: '130px' },
  ],
  fields: [
    { key: 'user_id', label: 'User', required: true, immutable: true },
    { key: 'status', label: 'Status', type: 'select', required: true,
      options: [{ value: 'Active', label: 'Active' },
                { value: 'Suspended', label: 'Suspended' }] },
    { key: 'valid_from', label: 'Valid From', type: 'date',
      help: 'Leave blank for immediate' },
    { key: 'valid_to', label: 'Valid To', type: 'date',
      help: 'Leave blank for open-ended' },
  ],
}

/** Units of measure — a global list, shared by both companies.
 *
 * An item has exactly one unit (ITEM_MASTER.md §2, confirmed by FKMT), so
 * there is nothing here about conversions. `decimals` is what stops someone
 * receiving 12.5 of something counted in numbers.
 */
export const UOMS: MasterSpec = {
  module: 'masters', object: 'uom', title: 'Units of Measure',
  singular: 'Unit of Measure',
  path: 'masters/uom', idKey: 'uom_code',
  searchPlaceholder: 'Search units…',
  columns: [
    { key: 'uom_code', label: 'Code', width: '110px' },
    { key: 'name',     label: 'Name' },
    { key: 'decimals', label: 'Decimals', width: '110px', numeric: true },
  ],
  fields: [
    { key: 'uom_code', label: 'Code', required: true, immutable: true,
      help: 'e.g. KG, NOS, MTR' },
    { key: 'name', label: 'Name', required: true },
    { key: 'decimals', label: 'Decimal Places', type: 'number',
      help: '0 for whole numbers, 3 for kilograms' },
  ],
}

/** Where stock physically is.
 *
 * `kind` carries more weight than it looks. Material at a subcontractor is
 * still FKMT's stock, at someone else's premises — a location, not an
 * absence. That is what keeps subcontract stock visible instead of vanishing
 * the moment it is issued.
 */
export const LOCATIONS: MasterSpec = {
  module: 'masters', object: 'location', title: 'Locations',
  singular: 'Location',
  path: 'masters/locations', idKey: 'location_id',
  searchPlaceholder: 'Search locations…',
  columns: [
    { key: 'location_id', label: 'Code', width: '140px' },
    { key: 'name',        label: 'Name' },
    { key: 'kind',        label: 'Kind', width: '140px' },
    { key: 'party_id',    label: 'Party', width: '140px' },
  ],
  fields: [
    { key: 'location_id', label: 'Code', required: true, immutable: true,
      help: 'e.g. FKLPT02, MAINSTOCK' },
    { key: 'name', label: 'Name', required: true },
    { key: 'kind', label: 'Kind', type: 'select', required: true,
      options: [
        { value: 'Store',         label: 'Store' },
        { value: 'ShopFloor',     label: 'Shop Floor' },
        { value: 'Subcontractor', label: 'Subcontractor' },
        { value: 'InTransit',     label: 'In Transit' },
        { value: 'Project',       label: 'Project' },
      ] },
    { key: 'party_id', label: 'Party',
      help: 'Only for a subcontractor location' },
  ],
}

export const MASTER_SCREENS: Record<string, MasterSpec> = {
  'admin/companies': COMPANIES,
  'admin/users':     USERS,
  'admin/financial-years': FINANCIAL_YEARS,
  'admin/company-access':  COMPANY_ACCESS,
  'admin/roles':     ROLES,
  'masters/uom':       UOMS,
  'masters/locations': LOCATIONS,
  'masters/items':          ITEMS,
  'masters/item-groups':    ITEM_GROUPS,
  'masters/item-subgroups': ITEM_SUBGROUPS,
  'masters/product-families': PRODUCT_FAMILIES,
  'masters/product-models':   PRODUCT_MODELS,
  'masters/tax-rates':        TAX_RATES,
  'masters/processes':      PROCESSES,
  'masters/stages':         STAGES,
  'masters/suppliers':      SUPPLIERS,
  'masters/customers':      CUSTOMERS,
}
